/** This file is used by the node server and the browser code */

const LINE_ITEM_NIGHT = 'line-item/night';
const LINE_ITEM_DAY = 'line-item/day';
const LINE_ITEM_UNITS = 'line-item/units';
const LINE_ITEM_CUSTOMER_COMMISSION = 'line-item/customer-commission';
const LINE_ITEM_PROVIDER_COMMISSION = 'line-item/provider-commission';
const LINE_ITEM_PARTY_SIZE = 'line-item/party-size';
const LINE_ITEM_NEGOTIATION = 'line-item/negotiation';
const LINE_ITEM_SPECIAL_OFFER = 'line-item/special-offer';
const LINE_ITEM_LODGING_TAX = 'line-item/lodging-tax';
const LINE_ITEM_LODGING_FEE = 'line-item/lodging-fee';
const LINE_ITEM_CONSERVATION_DONATION = 'line-item/conservation-donation';
const LINE_ITEM_PROMO_CODE = 'line-item/promo-code';
const LINE_ITEM_INSURANCE = 'line-item/insurance';
const LINE_ITEM_GUEST_LIABILITY_PROTECTION = 'line-item/guest-liability-protection';
const LINE_ITEM_LT_PLUS_GLP_WAIVER = 'line-item/lt-plus-glp-waiver';
const LINE_ITEM_ADULT_HUNTER = 'line-item/adult-hunter';
const LINE_ITEM_ADULT_NON_HUNTER = 'line-item/adult-non-hunter';
const LINE_ITEM_YOUTH_HUNTER = 'line-item/youth-hunter';
const LINE_ITEM_YOUTH_NON_HUNTER = 'line-item/youth-non-hunter';
const LINE_ITEMS = [LINE_ITEM_CUSTOMER_COMMISSION, LINE_ITEM_PROVIDER_COMMISSION];
const CUSTOMER_COMMISSION_PERCENTAGE = 15;
// fallback default percentage
const PROVIDER_COMMISSION_PERCENTAGE = -20;
const INSURANCE_PERCENTAGE = 6.95;

// Generali trip insurance is no longer offered during checkout. Historical
// bookings that already stored the line item and metadata still render it.
const TRIP_INSURANCE_CHECKOUT_ENABLED = false;

// Guest Liability Protection pricing. All money amounts below are in minor units (cents).
//
// fee = max(MIN_PRICE, PERCENTAGE% * booking subtotal)
//
// Booking subtotal is the customer-facing package total before the LandTrust booking
// fee (customer commission) and before GLP / trip insurance add-ons.
const GUEST_LIABILITY_PROTECTION_MIN_PRICE = 3600; // $36.00
const GUEST_LIABILITY_PROTECTION_PERCENTAGE = 4;

const LINE_ITEM_CODES_EXCLUDED_FROM_GLP_SUBTOTAL = [
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_INSURANCE,
  LINE_ITEM_GUEST_LIABILITY_PROTECTION,
  LINE_ITEM_LT_PLUS_GLP_WAIVER,
];

/**
 * Sums the booking subtotal used for Guest Liability Protection (minor units / cents).
 * Excludes the LandTrust booking fee and add-on line items.
 */
const getGuestLiabilityProtectionSubtotalAmount = lineItems => {
  if (!Array.isArray(lineItems)) {
    return 0;
  }

  return lineItems
    .filter(
      lineItem =>
        (!lineItem.includeFor || lineItem.includeFor.includes('customer')) &&
        !LINE_ITEM_CODES_EXCLUDED_FROM_GLP_SUBTOTAL.includes(lineItem.code) &&
        !lineItem.reversal
    )
    .reduce((sum, lineItem) => {
      const lineTotalAmount = lineItem.lineTotal?.amount ?? lineItem.unitPrice?.amount ?? 0;
      return sum + lineTotalAmount;
    }, 0);
};

/**
 * Calculate the Guest Liability Protection fee (in minor units / cents).
 *
 * @param {number} subtotalAmount Booking subtotal in minor units (cents), excluding booking fee.
 * @returns {number} Fee in minor units (integer cents).
 */
const calculateGuestLiabilityProtectionAmount = subtotalAmount => {
  const subtotal = Math.max(0, Math.round(Number(subtotalAmount) || 0));
  const percentageFee = Math.round((subtotal * GUEST_LIABILITY_PROTECTION_PERCENTAGE) / 100);

  return Math.max(GUEST_LIABILITY_PROTECTION_MIN_PRICE, percentageFee);
};

// depracated but still needed for old transaction line-items
const LINE_ITEM_ACCESS_FEE = 'line-item/access-fee';
const LINE_ITEM_FISHING_FEE = 'line-item/fishing-fee';

const PAYMENT_INTENT_TYPES = {
  DEFAULT: 'default',
  BOOKING_CHANGES: 'bookingChanges',
};

const shouldExcludeFromSubtotal = lineItemCode => {
  return [
    LINE_ITEM_CUSTOMER_COMMISSION,
    LINE_ITEM_PROVIDER_COMMISSION,
    LINE_ITEM_CONSERVATION_DONATION,
    LINE_ITEM_LODGING_TAX,
    LINE_ITEM_PROMO_CODE,
    LINE_ITEM_INSURANCE,
    LINE_ITEM_GUEST_LIABILITY_PROTECTION,
    LINE_ITEM_LT_PLUS_GLP_WAIVER,
  ].includes(lineItemCode);
};

module.exports = {
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_UNITS,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_NEGOTIATION,
  LINE_ITEM_PARTY_SIZE,
  LINE_ITEM_SPECIAL_OFFER,
  LINE_ITEM_LODGING_TAX,
  LINE_ITEM_LODGING_FEE,
  LINE_ITEM_CONSERVATION_DONATION,
  LINE_ITEM_PROMO_CODE,
  LINE_ITEM_INSURANCE,
  LINE_ITEM_GUEST_LIABILITY_PROTECTION,
  LINE_ITEM_LT_PLUS_GLP_WAIVER,
  LINE_ITEM_ADULT_HUNTER,
  LINE_ITEM_ADULT_NON_HUNTER,
  LINE_ITEM_YOUTH_HUNTER,
  LINE_ITEM_YOUTH_NON_HUNTER,
  LINE_ITEM_ACCESS_FEE,
  LINE_ITEM_FISHING_FEE,
  LINE_ITEMS,
  CUSTOMER_COMMISSION_PERCENTAGE,
  PROVIDER_COMMISSION_PERCENTAGE,
  INSURANCE_PERCENTAGE,
  TRIP_INSURANCE_CHECKOUT_ENABLED,
  GUEST_LIABILITY_PROTECTION_MIN_PRICE,
  GUEST_LIABILITY_PROTECTION_PERCENTAGE,
  getGuestLiabilityProtectionSubtotalAmount,
  calculateGuestLiabilityProtectionAmount,
  PAYMENT_INTENT_TYPES,
  shouldExcludeFromSubtotal,
};
